import dayjs from 'dayjs'
import utcPlugin from 'dayjs/plugin/utc'
import 'dayjs/locale/en'
import 'dayjs/locale/pt'
import type { Database } from 'types/supabase'
import type { DeskWatchedValues } from 'components/events/EventForm/types'

dayjs.extend(utcPlugin)

/**
 * The rows of the duplicate side-by-side (D-14, DESK-03) and the update
 * before/after (D-15, DESK-04): the closed set Title, Starts, Ends, Place,
 * Price, Status, each with display values and whether the two sides differ.
 *
 * Inputs are normalised first (`fromApplication`, `fromEvent`,
 * `fromQueueTarget`), so a candidate can be compared with a published event or
 * with another (pending) candidate through the same code.
 *
 * **Wall-clock rule.** `events.start_time` / `finish_time` hold the Lisbon
 * wall-clock digits stamped `+00:00` (21:00 on the poster is stored
 * `...T21:00:00+00:00`, summer or winter; web/src/lib/lisbonTime.ts, admin
 * `EventsList` reads them with `dayjs.utc`). A candidate's `starting_date` +
 * `starting_time` are the same wall clock with no zone. So a stored value is
 * read by its UTC components and compared as text, at minute precision, with
 * no zone conversion: '2026-11-07' + '21:00' equals '2026-11-07T21:00:00+00:00'.
 * Converting to Lisbon time would shift every summer event by an hour and hide
 * (or invent) a real change (T-33-40). If the deferred data migration ever
 * stores true instants, this reader changes with it (lisbonTime.ts flip point).
 *
 * Normalised comparison per row:
 * - title: trimmed, whitespace collapsed, case-folded; an event has two titles
 *   (en and pt) and matches when either equals the other side's title;
 * - start / end: date + time at minute precision (a date with no time differs
 *   from a timed one); a candidate end time with no end date reads the start date;
 * - place: by venue id when both sides have one, else by city id, else by text;
 * - price: trimmed (empty = none);
 * - status: `scheduled` or `cancelled`. A candidate is `cancelled` when its
 *   route_checks hold `status_change` (shown as "The post says cancelled or
 *   postponed"); an event when `cancelled` is true.
 *
 * Display values are strings or null (null renders as `desk.empty_value`, "—");
 * status is a translation key. Pure: no Supabase, no React, no translation lookup.
 */

export const COMPARE_FIELDS = ['title', 'start', 'end', 'place', 'price', 'status'] as const

export type CompareField = (typeof COMPARE_FIELDS)[number]

export type WallClock = { date: string; time: string | null }

export type CompareStatus = 'scheduled' | 'cancelled'

export type CompareStatusKey = 'desk.status.scheduled' | 'desk.status.cancelled' | 'desk.checks.status_change'

export type CompareSide = {
  /** The titles this side answers to; the first is displayed. */
  titles: string[]
  start: WallClock | null
  end: WallClock | null
  place: { venueId: number | null; cityId: number | null; text: string | null }
  /** Display text for the place (a resolved label, else the side's own text). */
  placeLabel: string | null
  price: string | null
  status: CompareStatus
  statusKey: CompareStatusKey
}

export type CompareCell = { text: string | null; key: string | null }

export type CompareRow = { field: CompareField; left: CompareCell; right: CompareCell; differs: boolean }

type EventRow = Database['public']['Tables']['events']['Row']
type DeskQueueRow = Database['public']['Views']['desk_queue']['Row']

/** The candidate columns compared: an applications row and a desk_queue row both fit. */
export type ApplicationCompareSource = {
  event_title: string | null
  starting_date: string | null
  starting_time: string | null
  ending_date: string | null
  ending_time: string | null
  venue_id: number | null
  city_id: number | null
  event_venue_location: string | null
  venue_village: string | null
  price: string | null
  route_checks: readonly string[] | null
}

export type EventCompareSource = Pick<EventRow,
  'title' | 'title_pt' | 'start_time' | 'finish_time' | 'venue' | 'city_id' | 'venue_name' | 'venue_city' | 'price' | 'cancelled'>

export type QueueTargetCompareSource = Pick<DeskQueueRow,
  'target_event_title' | 'target_event_title_pt' | 'target_event_start_time' | 'target_event_finish_time' |
  'target_event_venue' | 'target_event_city_id' | 'target_event_price' | 'target_event_cancelled'>

const clean = (value: string | null | undefined): string | null => {
  const text = value?.trim().replace(/\s+/g, ' ')
  return text ? text : null
}

const foldTitle = (value: string): string => value.toLocaleLowerCase('pt-PT')

const joinPlace = (...parts: Array<string | null | undefined>): string | null => {
  const kept = parts.map(clean).filter((part): part is string => part !== null)
  return kept.length > 0 ? kept.join(' · ') : null
}

const pad = (value: number): string => String(value).padStart(2, '0')

const DATE = /^(\d{4}-\d{2}-\d{2})/
const TIME = /^(\d{2}):(\d{2})/
const STORED = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}(?::?\d{2})?)?$/

/**
 * A stored events timestamp as wall clock: its UTC digits (see the docblock).
 * A value with no offset is taken as wall clock as is; null when unreadable.
 */
export const readStoredWallClock = (value: string | null | undefined): WallClock | null => {
  if (!value) return null
  const match = STORED.exec(value.trim())
  if (!match) return null
  const [, date, hours, minutes, offset] = match
  if (!offset || offset === 'Z' || /^\+00(:?00)?$/.test(offset)) return { date, time: `${hours}:${minutes}` }

  const normalisedOffset = offset.length === 3 ? `${offset}:00` : offset.replace(/^([+-]\d{2})(\d{2})$/, '$1:$2')
  const instant = new Date(`${date}T${hours}:${minutes}:00${normalisedOffset}`)
  if (Number.isNaN(instant.getTime())) return null
  return {
    date: `${instant.getUTCFullYear()}-${pad(instant.getUTCMonth() + 1)}-${pad(instant.getUTCDate())}`,
    time: `${pad(instant.getUTCHours())}:${pad(instant.getUTCMinutes())}`
  }
}

/** A candidate's date + optional time columns as wall clock; null with no date. */
const readCandidateWallClock = (date: string | null, time: string | null): WallClock | null => {
  const day = date ? DATE.exec(date.trim()) : null
  if (!day) return null
  const clock = time ? TIME.exec(time.trim()) : null
  return { date: day[1], time: clock ? `${clock[1]}:${clock[2]}` : null }
}

export const fromApplication = (row: ApplicationCompareSource, placeLabel?: string | null): CompareSide => {
  const cancelled = (row.route_checks ?? []).includes('status_change')
  const title = clean(row.event_title)
  const placeText = joinPlace(row.event_venue_location, row.venue_village)
  return {
    titles: title ? [title] : [],
    start: readCandidateWallClock(row.starting_date, row.starting_time),
    end: readCandidateWallClock(row.ending_date ?? (row.ending_time ? row.starting_date : null), row.ending_time),
    place: { venueId: row.venue_id, cityId: row.city_id, text: placeText },
    placeLabel: placeLabel ?? placeText,
    price: clean(row.price),
    status: cancelled ? 'cancelled' : 'scheduled',
    statusKey: cancelled ? 'desk.checks.status_change' : 'desk.status.scheduled'
  }
}

/** The form values a desk re-check matched with (D-04): town, start, end. */
export type FormCompareValues = Pick<DeskWatchedValues, 'cityId' | 'startTime' | 'matchFields'>

/**
 * Code review WR-03: a desk re-check (match_by = desk_rules) matched on the
 * form's town and dates, which are not saved before Approve (D-04). The Match
 * panel's left column is therefore the row with those columns taken from the
 * form, so "Place" and "Starts" show what the rules compared. Dates are the
 * form's Lisbon wall-clock digits stamped UTC (D-25), so they are read by
 * their UTC components. `placeholders` are deskPrefill's START/END_TIME
 * placeholders when the post did not state that time (timeNotStated), else
 * null: a time still on its placeholder reads as no time, as on the row.
 * Without `matchFields` (an older report) the row's end is kept. Every other
 * column, the venue included, is the row's.
 */
export const withFormValues = <R extends ApplicationCompareSource>(
  row: R,
  form: FormCompareValues,
  placeholders: { start: string | null; end: string | null }
): R => {
  const wallClock = (value: Date | null, placeholder: string | null): { date: string | null; time: string | null } => {
    const read = value ? dayjs.utc(value) : null
    if (!read || !read.isValid()) return { date: null, time: null }
    const time = read.format('HH:mm:ss')
    return { date: read.format('YYYY-MM-DD'), time: placeholder !== null && time === placeholder ? null : read.format('HH:mm') }
  }
  const start = wallClock(form.startTime, placeholders.start)
  const end = form.matchFields
    ? wallClock(form.matchFields.finishTime, placeholders.end)
    : { date: row.ending_date, time: row.ending_time }
  return {
    ...row,
    city_id: form.cityId,
    starting_date: start.date,
    starting_time: start.time,
    ending_date: end.date,
    ending_time: end.time
  }
}

const fromEventColumns = (columns: {
  title: string | null
  titlePt: string | null
  start: string | null
  finish: string | null
  venueId: number | null
  cityId: number | null
  placeText: string | null
  price: string | null
  cancelled: boolean | null
}, placeLabel?: string | null): CompareSide => {
  const titles = [clean(columns.title), clean(columns.titlePt)].filter((title): title is string => title !== null)
  return {
    titles,
    start: readStoredWallClock(columns.start),
    end: readStoredWallClock(columns.finish),
    place: { venueId: columns.venueId, cityId: columns.cityId, text: columns.placeText },
    placeLabel: placeLabel ?? columns.placeText,
    price: clean(columns.price),
    status: columns.cancelled === true ? 'cancelled' : 'scheduled',
    statusKey: columns.cancelled === true ? 'desk.status.cancelled' : 'desk.status.scheduled'
  }
}

export const fromEvent = (row: EventCompareSource, placeLabel?: string | null): CompareSide =>
  fromEventColumns({
    title: row.title,
    titlePt: row.title_pt,
    start: row.start_time,
    finish: row.finish_time,
    venueId: row.venue,
    cityId: row.city_id,
    placeText: joinPlace(row.venue_name, row.venue_city),
    price: row.price,
    cancelled: row.cancelled
  }, placeLabel)

/** The desk_queue target_event_* columns carry ids only, so the place shows the label passed in. */
export const fromQueueTarget = (row: QueueTargetCompareSource, placeLabel?: string | null): CompareSide =>
  fromEventColumns({
    title: row.target_event_title,
    titlePt: row.target_event_title_pt,
    start: row.target_event_start_time,
    finish: row.target_event_finish_time,
    venueId: row.target_event_venue,
    cityId: row.target_event_city_id,
    placeText: null,
    price: row.target_event_price,
    cancelled: row.target_event_cancelled
  }, placeLabel)

const wallClockKey = (value: WallClock | null): string | null =>
  value ? `${value.date}T${value.time ?? ''}` : null

/** The dayjs locale a desk date is written in: the weekday follows the admin language (D-22). */
export type DeskDateLocale = 'en' | 'pt'

/** The admin language ('en', 'pt-PT', a detected 'en-US'...) as a dayjs locale. */
export const dateLocaleOf = (language: string | null | undefined): DeskDateLocale =>
  language?.toLowerCase().startsWith('pt') ? 'pt' : 'en'

const formatWallClock = (value: WallClock | null, locale: DeskDateLocale): string | null => {
  if (!value) return null
  return value.time
    ? dayjs(`${value.date}T${value.time}`).locale(locale).format('ddd, DD/MM/YYYY HH:mm')
    : dayjs(value.date).locale(locale).format('ddd, DD/MM/YYYY')
}

const titlesDiffer = (left: string[], right: string[]): boolean => {
  if (left.length === 0 || right.length === 0) return left.length !== right.length
  const folded = new Set(right.map(foldTitle))
  return !left.some((title) => folded.has(foldTitle(title)))
}

const placeIsEmpty = (place: CompareSide['place']): boolean =>
  place.venueId === null && place.cityId === null && place.text === null

const placesDiffer = (left: CompareSide['place'], right: CompareSide['place']): boolean => {
  if (placeIsEmpty(left) || placeIsEmpty(right)) return placeIsEmpty(left) !== placeIsEmpty(right)
  if (left.venueId !== null && right.venueId !== null) return left.venueId !== right.venueId
  if (left.cityId !== null && right.cityId !== null) return left.cityId !== right.cityId
  return (left.text === null ? null : foldTitle(left.text)) !== (right.text === null ? null : foldTitle(right.text))
}

const textCell = (text: string | null): CompareCell => ({ text, key: null })

const rowFor = (field: CompareField, left: CompareSide, right: CompareSide, locale: DeskDateLocale): CompareRow => {
  switch (field) {
    case 'title':
      return { field, left: textCell(left.titles[0] ?? null), right: textCell(right.titles[0] ?? null), differs: titlesDiffer(left.titles, right.titles) }
    case 'start':
      return { field, left: textCell(formatWallClock(left.start, locale)), right: textCell(formatWallClock(right.start, locale)), differs: wallClockKey(left.start) !== wallClockKey(right.start) }
    case 'end':
      return { field, left: textCell(formatWallClock(left.end, locale)), right: textCell(formatWallClock(right.end, locale)), differs: wallClockKey(left.end) !== wallClockKey(right.end) }
    case 'place':
      return { field, left: textCell(left.placeLabel), right: textCell(right.placeLabel), differs: placesDiffer(left.place, right.place) }
    case 'price':
      return { field, left: textCell(left.price), right: textCell(right.price), differs: left.price !== right.price }
    case 'status':
      return { field, left: { text: null, key: left.statusKey }, right: { text: null, key: right.statusKey }, differs: left.status !== right.status }
  }
}

/**
 * The six rows, in COMPARE_FIELDS order (D-14 side-by-side shows them all).
 * `locale` only writes the dates' weekday (en by default); `differs` never depends on it.
 */
export const sideBySideRows = (left: CompareSide, right: CompareSide, locale: DeskDateLocale = 'en'): CompareRow[] =>
  COMPARE_FIELDS.map((field) => rowFor(field, left, right, locale))

/** Only the fields that differ, in COMPARE_FIELDS order (D-15 before/after lists only these). */
export const proposalChanges = (left: CompareSide, right: CompareSide): CompareField[] =>
  sideBySideRows(left, right).filter((row) => row.differs).map((row) => row.field)
